import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, GitBranch, Pencil, MapPin, Phone, Mail, UserCog, ChevronRight, Globe } from "lucide-react";
import { toast } from "sonner";

const COUNTRIES = ["USA", "Canada"];
const emptyForm = {
  branch_name: "", name: "", code: "", country: "USA", state_province: "", city_region: "",
  manager_user_id: "", phone: "", email: "", service_area: "", status: "active", notes: "",
};

export default function Branches() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const canManage = ["super_admin", "national_admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/branches"); setItems(data.items || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data.items || r.data || [])).catch(() => setUsers([])); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...emptyForm, ...b, branch_name: b.branch_name || b.name || "", city_region: b.city_region || b.city || "", state_province: b.state_province || b.state || "" });
    setOpen(true);
  };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.branch_name.trim()) { toast.error("Branch name is required"); return; }
    setSubmitting(true);
    const payload = { ...form, name: form.branch_name };
    Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
    try {
      if (editing) await api.put(`/branches/${editing.id}`, payload);
      else await api.post("/branches", payload);
      toast.success(editing ? "Branch updated" : "Branch created");
      setOpen(false); await load(); refresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); } finally { setSubmitting(false); }
  };

  // Group by Country -> State/Province
  const grouped = {};
  for (const b of items) {
    const country = b.country || "Other";
    const state = b.state_province || b.state || "Other region";
    grouped[country] = grouped[country] || {};
    grouped[country][state] = grouped[country][state] || [];
    grouped[country][state].push(b);
  }
  const managerName = (id) => users.find((u) => u.id === id)?.full_name;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2"><GitBranch size={22} className="text-[#1C4358]" /> Branches & Franchises</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Expand across the USA & Canada — organized by country, state/province, and city.</p>
        </div>
        {canManage && (
          <Button onClick={openNew} data-testid="new-branch-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
            <Plus size={17} className="mr-1.5" /> New Branch
          </Button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40" />)}</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center rounded-xl border border-[#E6EDF2] bg-white">
          <div className="mx-auto mb-3 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}><GitBranch size={26} className="text-[#1C4358]" /></div>
          <p className="font-medium text-[#252221]">No branches yet</p>
          <p className="text-sm text-muted-foreground">Add your first branch to start expanding the MovingMyMama network.</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="branch-hierarchy">
          {Object.entries(grouped).map(([country, states]) => (
            <div key={country}>
              <div className="flex items-center gap-2 mb-3">
                <Globe size={16} className="text-[#1C4358]" />
                <h3 className="font-display text-lg font-bold text-[#252221]">{country}</h3>
                <span className="text-xs text-muted-foreground">({Object.values(states).flat().length} branches)</span>
              </div>
              {Object.entries(states).map(([state, branches]) => (
                <div key={state} className="mb-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1.5"><MapPin size={12} /> {state}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {branches.map((b) => (
                      <div key={b.id} onClick={() => navigate(`/branches/${b.id}`)}
                        className="cursor-pointer rounded-xl border border-[#E6EDF2] bg-white p-5 transition-shadow hover:shadow-md group"
                        style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="branch-card">
                        <div className="flex items-start justify-between">
                          <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.10)" }}>
                            <GitBranch size={20} className="text-[#1C4358]" />
                          </div>
                          <div className="flex items-center gap-2">
                            {b.is_pilot && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: "rgba(222,224,41,0.35)", color: "#5A5E2E" }}>PILOT</span>}
                            <StatusBadge value={b.status} />
                          </div>
                        </div>
                        <div className="mt-3 font-display font-semibold text-lg text-[#252221] flex items-center gap-1">{b.branch_name || b.name}<ChevronRight size={16} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin size={13} /> {[b.city_region || b.city, b.state_province || b.state, b.country].filter(Boolean).join(", ")}</div>
                        <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                          {(b.manager_user_id && managerName(b.manager_user_id)) && <div className="flex items-center gap-1.5"><UserCog size={12} /> {managerName(b.manager_user_id)}</div>}
                          {b.phone && <div className="flex items-center gap-1.5"><Phone size={12} /> {b.phone}</div>}
                          {b.email && <div className="flex items-center gap-1.5"><Mail size={12} /> {b.email}</div>}
                          {b.service_area && <div className="flex items-center gap-1.5"><Globe size={12} /> {b.service_area}</div>}
                        </div>
                        {canManage && (
                          <Button size="sm" variant="outline" className="mt-4 w-full" onClick={(e) => { e.stopPropagation(); openEdit(b); }} data-testid="edit-branch-button">
                            <Pencil size={14} className="mr-1.5" /> Edit
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto mmm-scroll" data-testid="branch-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit Branch" : "New Branch"}</DialogTitle>
            <DialogDescription>Define the branch location, manager, and service area.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="mb-1.5 block text-[13px] font-medium">Branch Name *</Label>
                <Input value={form.branch_name} onChange={(e) => set("branch_name", e.target.value)} data-testid="branch-name" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Country</Label>
                <Select value={form.country} onValueChange={(v) => set("country", v)}>
                  <SelectTrigger data-testid="branch-country"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">State / Province</Label>
                <Input value={form.state_province} onChange={(e) => set("state_province", e.target.value)} placeholder="e.g. Missouri" data-testid="branch-state" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">City / Region</Label>
                <Input value={form.city_region} onChange={(e) => set("city_region", e.target.value)} placeholder="e.g. Kansas City" data-testid="branch-city" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Branch Code</Label>
                <Input value={form.code || ""} onChange={(e) => set("code", e.target.value.toUpperCase())} data-testid="branch-code" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Branch Manager</Label>
                <Select value={form.manager_user_id || "none"} onValueChange={(v) => set("manager_user_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="branch-manager"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="max-h-[260px]"><SelectItem value="none">— None —</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="branch-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="launching">Launching</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Phone</Label>
                <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="branch-phone" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px] font-medium">Email</Label>
                <Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="branch-email" />
              </div>
              <div className="sm:col-span-2">
                <Label className="mb-1.5 block text-[13px] font-medium">Service Area</Label>
                <Input value={form.service_area || ""} onChange={(e) => set("service_area", e.target.value)} placeholder="e.g. Greater Kansas City metro" data-testid="branch-service-area" />
              </div>
              <div className="sm:col-span-2">
                <Label className="mb-1.5 block text-[13px] font-medium">Notes</Label>
                <Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="branch-notes" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="branch-submit-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>{submitting ? "Saving..." : "Save"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
